import { getAuthors, type Author } from "~/data/authors"
import type { Post } from "~/data/posts"
import { cn } from "~/lib/utils"

// The author (and, where relevant, interview guest) credit — the first thing
// in the article body, with the post's own text underneath. No "Written by"
// label on the byline itself — the avatar and name already read as one;
// "Interviewed" stays labeled since that's the one relationship that isn't
// self-evident.

// When every author in the row shares the same role (the three Pine59
// co-founders), that role is redundant repeated three times — say it once
// for the group instead, and drop it from each person's own line. Also lets
// each cell run shorter, so a trio fits as a grid of three instead of
// wrapping 2-then-1.
function sharedRole(authors: Author[]): string | undefined {
  if (authors.length < 2) return undefined
  const [first, ...rest] = authors
  return rest.every((a) => a.role === first.role) ? first.role : undefined
}

function pluralizeRole(role: string): string {
  const [title, ...org] = role.split(", ")
  return [`${title}s`, ...org].join(", ")
}

function AuthorRow({
  label,
  authors,
  showLabel = true,
}: {
  label: string
  authors: Author[]
  showLabel?: boolean
}) {
  if (authors.length === 0) return null
  const groupRole = sharedRole(authors)
  const eyebrow = groupRole ? pluralizeRole(groupRole) : showLabel ? label : null

  return (
    <div>
      {eyebrow ? <p className="u-eyebrow">{eyebrow}</p> : null}
      <div
        className={cn(
          "flex flex-col gap-5",
          authors.length > 2
            ? "sm:grid sm:grid-cols-3 sm:gap-6"
            : "sm:flex-row sm:flex-wrap sm:gap-10",
          eyebrow && "mt-5",
        )}
      >
        {authors.map((author) => (
          <div key={author.name} className="flex items-center gap-3.5">
            {author.avatar ? (
              <img
                src={author.avatar}
                alt=""
                className="h-14 w-14 shrink-0 rounded-full object-cover"
              />
            ) : (
              <span
                aria-hidden
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-forest font-serif text-[17px] italic text-lime"
              >
                {author.initials}
              </span>
            )}
            <p className="font-sans text-[15px] leading-snug text-ink">
              <span className="font-medium">{author.name}</span>
              {!groupRole && author.role ? (
                <span className="text-mute"> · {author.role}</span>
              ) : null}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

export function WrittenBy({ post }: { post: Post }) {
  const authors = getAuthors(post.authors)
  const interviewees = getAuthors(post.interviewees)
  if (authors.length === 0 && interviewees.length === 0) return null

  return (
    <div className="mb-8 flex flex-col gap-5 border-b border-line pb-6">
      <AuthorRow label="Written by" authors={authors} showLabel={false} />
      <AuthorRow label="Interviewed" authors={interviewees} />
    </div>
  )
}
